/**
 * Product Nutritional Information Handler
 * Handles the display and updating of nutritional information for product variants
 */

class NutritionalInformationHandler {
  constructor(options = {}) {
    this.containerId = options.containerId || 'nutritional-information-container';
    this.container = document.getElementById(this.containerId);
    this.productData = options.productData || {};
    this.variantMetafields = options.variantMetafields || {};
    this.debugMode = options.debugMode || false;
    this.translations = options.translations || {};
    
    this.init();
  }
  
  init() {
    if (!this.container) {
      return;
    }
    
    // Set up event listeners for variant changes
    this.setupEventListeners();
    
    // No need to update on initial load as Liquid has already rendered the content
  }
  
  setupEventListeners() {
    if (this.debugMode) console.log('Setting up event listeners for variant changes');
    
    // Listen for the variant change event that works for this theme
    document.addEventListener('on:variant:change', (event) => {
      if (this.debugMode) console.log('on:variant:change event detected:', event);
      
      // Get the variant ID from the form
      setTimeout(() => {
        const variantId = document.querySelector('input[name="id"]')?.value;
        if (variantId) {
          if (this.debugMode) console.log('Variant ID from form:', variantId);
          // Convert to number to ensure consistent type comparison
          const numericVariantId = parseInt(variantId, 10);
          if (this.debugMode) console.log('Numeric Variant ID:', numericVariantId);
          this.updateNutritionalInfo(numericVariantId);
        }
      });
    });
    
    // Also listen for variant:changed event (used by some themes)
    document.addEventListener('variant:changed', (event) => {
      if (this.debugMode) console.log('variant:changed event detected:', event);
      if (event.detail && event.detail.variant && event.detail.variant.id) {
        const numericVariantId = parseInt(event.detail.variant.id, 10);
        this.updateNutritionalInfo(numericVariantId);
      }
    });
    
    // Set up MutationObserver for variant changes
    const productForm = document.querySelector('form[action*="/cart/add"]');
    if (productForm) {
      const variantIdInput = productForm.querySelector('input[name="id"]');
      if (variantIdInput) {
        // Create a MutationObserver to watch for changes to the variant ID input
        const observer = new MutationObserver((mutations) => {
          for (const mutation of mutations) {
            if (mutation.type === 'attributes' && mutation.attributeName === 'value') {
              const variantId = variantIdInput.value;
              // Convert to number for consistent type comparison
              const numericVariantId = parseInt(variantId, 10);
              if (this.debugMode) console.log('Variant ID changed via MutationObserver:', variantId, 'Numeric:', numericVariantId);
              this.updateNutritionalInfo(numericVariantId);
              break;
            }
          }
        });
        
        // Start observing the variant ID input for attribute changes
        observer.observe(variantIdInput, { attributes: true });
        
        if (this.debugMode) console.log('MutationObserver set up for variant ID input');
      }
    }
  }
  
  updateNutritionalInfo(variantId) {
    // Force debug mode on temporarily
    console.log('VARIANT CHANGED - updateNutritionalInfo called with variantId:', variantId);
    
    // Add a visible indicator that variant change was detected
    this.container.innerHTML = `<div style="color: red; font-weight: bold;">Variant changed to: ${  variantId  }</div>`;
    
    if (!variantId || !this.container) {
      console.log('Missing variantId or container, returning');
      return;
    }
    
    // Get the nutritional data for this variant
    const nutritionalData = this.variantMetafields[variantId];
    console.log('Nutritional data for variant:', nutritionalData);
    
    // Clear previous content except our debug message
    // this.container.innerHTML = '';
    let html = '';
    
    if (!nutritionalData) {
      if (this.debugMode) console.log('No nutritional data found for variant', variantId);
      this.container.innerHTML = `<p>${this.translations.no_variant_information || 'No nutritional information available for this variant.'}</p>`;
      return;
    }
    
    // Check if the nutritional data has an error
    if (nutritionalData.error) {
      console.error('Error in nutritional data:', nutritionalData.error);
      this.container.innerHTML = `<p>${this.translations.error_loading || 'Error loading nutritional information:'} ${nutritionalData.error}</p>`;
      return;
    }
    
    // Add title, flavor, description if available
    if (nutritionalData.title || nutritionalData.flavor || nutritionalData.description) {
      html += '<div class="nutrition__product-info">';
      
      if (nutritionalData.title) {
        html += `<h3 class="nutrition__product-title">${nutritionalData.title}</h3>`;
      }
      if (nutritionalData.flavor) {
        html += `<p class="nutrition__product-flavor">${nutritionalData.flavor}</p>`;
      }
      if (nutritionalData.description) {
        html += `<p class="nutrition__product-description">${nutritionalData.description}</p>`;
      }
      
      html += '</div>';
    }
    
    // Add header with updated date if available
    html += '<div class="nutrition__header">';
    if (nutritionalData.updated_at) {
      html += `<p class="nutrition__updated">${this.translations.updated_at || 'Updated at'}: ${nutritionalData.updated_at}</p>`;
    }
    html += '</div>';
    
    // Add nutritional table if available
    if (nutritionalData.nutritional_facts) {
      if (this.debugMode) console.log('Rendering nutritional facts table');
      html += '<div class="nutrition__table-container"><table class="nutrition__table"><thead class="nutrition__table-head"><tr class="nutrition__table-row">';
      
      // Add table heading
      html += `<th class="nutrition__table-header">${nutritionalData.nutritional_facts.table_heading || this.translations.nutritional_facts || 'Nutritional Facts'}</th>`;
      
      // Add column headers
      if (Array.isArray(nutritionalData.nutritional_facts.columns)) {
        nutritionalData.nutritional_facts.columns.forEach(column => {
          html += `<th class="nutrition__table-header">${column.name}</th>`;
        });
      } else {
        console.error('Columns is not an array:', nutritionalData.nutritional_facts.columns);
      }
      
      html += '</tr></thead><tbody>';
      
      // Add nutrients
      if (Array.isArray(nutritionalData.nutritional_facts.nutrients)) {
        nutritionalData.nutritional_facts.nutrients.forEach(nutrient => {
          html += `<tr class="nutrition__table-row ${nutrient.is_main ? 'nutrition__table-row--main' : ''}">`;
          html += `<td class="nutrition__table-cell">${nutrient.name}</td>`;
          
          // Add values for each column
          if (Array.isArray(nutritionalData.nutritional_facts.columns)) {
            nutritionalData.nutritional_facts.columns.forEach(column => {
              html += `<td class="nutrition__table-cell">${nutrient[column.key] || '-'}</td>`;
            });
          }
          
          html += '</tr>';
          
          // Add sub-nutrients if any
          if (nutrient.sub_nutrients && Array.isArray(nutrient.sub_nutrients) && nutrient.sub_nutrients.length > 0) {
            nutrient.sub_nutrients.forEach(subNutrient => {
              html += `<tr class="nutrition__table-row nutrition__table-row--sub">`;
              html += `<td class="nutrition__table-cell nutrition__table-cell--sub">${subNutrient.name}</td>`;
              
              if (Array.isArray(nutritionalData.nutritional_facts.columns)) {
                nutritionalData.nutritional_facts.columns.forEach(column => {
                  html += `<td class="nutrition__table-cell">${subNutrient[column.key] || '-'}</td>`;
                });
              }
              
              html += '</tr>';
            });
          }
        });
      } else {
        console.error('Nutrients is not an array:', nutritionalData.nutritional_facts.nutrients);
      }
      
      html += '</tbody></table></div>';
    } else {
      if (this.debugMode) console.log('No nutritional_facts found in data');
    }
    
    // Add ingredients if available
    if (nutritionalData.ingredients) {
      html += `<div class="nutrition__section">
        <h4 class="nutrition__section-title">${this.translations.ingredients || 'Ingredients'}</h4>
        <p class="nutrition__section-content">${nutritionalData.ingredients}</p>
      </div>`;
    }
    
    // Add allergens if available
    if (nutritionalData.allergens) {
      html += `<div class="nutrition__section">
        <h4 class="nutrition__section-title">${this.translations.allergy_advice || 'Allergy Advice'}</h4>
        <p class="nutrition__section-content">${nutritionalData.allergens}</p>
      </div>`;
    }
    
    // Add usage instructions if available
    if (nutritionalData.usage && nutritionalData.usage.length > 0) {
      html += `<div class="nutrition__section">
        <h4 class="nutrition__section-title">${this.translations.recommended_use || 'Recommended Use'}</h4>
        <ol class="nutrition__usage-list">`;
      nutritionalData.usage.forEach(instruction => {
        html += `<li class="nutrition__usage-item">${instruction}</li>`;
      });
      html += `</ol>
      </div>`;
    }
    
    // Add storage advice if available
    if (nutritionalData.storage) {
      html += `<div class="nutrition__section">
        <h4 class="nutrition__section-title">${this.translations.storage_advice || 'Storage Advice'}</h4>
        <p class="nutrition__section-content">${nutritionalData.storage}</p>
      </div>`;
    }
    
    // Add net content if available
    if (nutritionalData.net_content) {
      html += `<div class="nutrition__section">
        <h4 class="nutrition__section-title">${this.translations.net_content || 'Net Content'}</h4>
        <p class="nutrition__section-content">${nutritionalData.net_content}</p>
      </div>`;
    }
    
    // Log the HTML for debugging
    if (this.debugMode) {
      console.log('Generated HTML length:', html.length);
      if (html.length < 100) {
        console.log('Generated HTML (short):', html);
      }
    }
    
    // Update the container with the HTML
    this.container.innerHTML = html || `<p>${this.translations.no_information || 'No nutritional information to display.'}</p>`;
    this.container.style.display = 'block';
    
    // Add a debug message at the bottom if in debug mode
    if (this.debugMode) {
      this.container.innerHTML += `<p class="debug-info">${this.translations.updated_at || 'Updated at'} ${new Date().toLocaleTimeString()} ${this.translations.for_variant || 'for variant'} ${variantId}</p>`;
    }
  }
}

// Make the class available globally
window.NutritionalInformationHandler = NutritionalInformationHandler;
